import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type GridColDef } from '@mui/x-data-grid';
import { useServerSearch, useServerListParams, useListDeleteHandler } from '@crm/shared';
import { ServerDataGrid } from '../../components/ServerDataGrid';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchBusinesses, deleteBusiness, createBusiness } from './businessThunks';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useConfirmDialog } from '../../components/useConfirmDialog';
import { useToast } from '../../components/useToast';
import { ImportButton } from '../../components/ImportButton';
import { SearchGlyph, PlusGlyph, ImportGlyph, EditGlyph, TrashGlyph } from '../../components/icons';
import type { BusinessCreatePayload } from './businessTypes';

interface BusinessListProps {
  onCreateClick: () => void;
  onEditClick?: (businessId: number) => void;
}

export default function BusinessList({ onCreateClick, onEditClick }: BusinessListProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const confirm = useConfirmDialog();
  const toast = useToast();
  const { items, status, error, totalCount } = useAppSelector((state) => state.businesses);
  // Server-side search condivisa (min 3 char + debounce 300ms).
  const { search, setSearch, query } = useServerSearch();
  // Pagination + sort server-side comuni a tutte le liste.
  const {
    paginationModel, setPaginationModel,
    sortModel, setSortModel,
    resetPage,
    backendParams,
  } = useServerListParams();

  // Reset page=0 quando cambia il filtro search (altrimenti puoi finire
  // oltre totalCount).
  useEffect(() => { resetPage(); }, [query, resetPage]);

  useEffect(() => {
    dispatch(fetchBusinesses({ ...backendParams, search: query || undefined }));
  }, [backendParams, query, dispatch]);

  // Delete + refetch coerente con totalCount/paginazione (vedi TS-9).
  const deleteAndRefetch = useListDeleteHandler({
    deleteThunk: deleteBusiness,
    fetchThunk: fetchBusinesses,
    fetchParams: { ...backendParams, search: query || undefined },
    itemsLength: items.length,
    page: paginationModel.page,
    resetPage,
    dispatch,
  });

  // Il rifiuto va mostrato: il server nega la cancellazione di un'azienda che
  // ha ancora preventivi (TS-103), e senza toast il click non farebbe nulla.
  async function handleDelete(businessId: number) {
    try {
      await deleteAndRefetch(businessId);
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  if (status === 'failed') return <p className="text-error">{t('common.error', { message: error })}</p>;

  return (
    <div>
      {/* Testata come la lista Contatti, che e' la pagina di riferimento del
          nuovo design: titolo e conteggio a sinistra, ricerca e azioni a
          destra, il tutto fuori dalla card. */}
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('business.title')}</h1>
          <span className="page-sub">{t('common.record', { count: totalCount })}</span>
        </div>
        <div className="page-actions">
          <div className="searchline">
            <SearchGlyph />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('business.searchPlaceholder')}
              aria-label={t('business.searchPlaceholder')}
            />
          </div>
          <ImportButton
            entityLabel={t('business.title').toLowerCase()}
            className="btn-ghost"
            icon={<ImportGlyph />}
            onEntities={async (items) => {
              for (const raw of items) {
                await dispatch(createBusiness(raw as BusinessCreatePayload)).unwrap();
              }
            }}
          />
          <button type="button" className="btn-primary" onClick={onCreateClick}>
            <PlusGlyph /> {t('business.newBusiness')}
          </button>
        </div>
      </div>

      <div className="list-card">
        {status === 'loading' && <p className="list-message">{t('common.loading')}</p>}
        {status === 'succeeded' && items.length === 0 && (
          <p className="text-muted list-message">{t('business.noBusiness')}</p>
        )}
        {items.length > 0 && (
          <ServerDataGrid
            rows={items}
            columns={businessColumns(confirm, t, handleDelete, onEditClick)}
            getRowId={(row) => row.businessId}
            rowCount={totalCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            loading={status === 'loading'}
            getRowHeight={() => 64}
            className="tk-grid"
          />
        )}
      </div>
      <ConfirmDialog open={confirm.isOpen} message={confirm.message} {...confirm.options} onConfirm={confirm.confirm} onCancel={confirm.cancel} />
    </div>
  );
}

function businessColumns(
  confirm: ReturnType<typeof useConfirmDialog>,
  t: (key: string, opts?: Record<string, unknown>) => string,
  handleDelete: (businessId: number) => void,
  onEditClick?: (businessId: number) => void,
): GridColDef[] {
  return [
    {
      field: 'externalCode', headerName: t('business.columns.externalCode'),
      width: 200,
      // Come nella lista contatti: qui solo il codice, l'avatar sta accanto al
      // nome. Uso || per intercettare anche le stringhe vuote, non solo null.
      renderCell: (params) => (
        <Link to={`/companies/${params.row.businessId}`} className="lc-code">
          {params.value || '—'}
        </Link>
      ),
    },
    {
      field: 'name', headerName: t('business.columns.name'), flex: 1,
      renderCell: (params) => (
        <span className="lc-name">
          <span className="lc-av">{(params.row.name?.charAt(0) ?? '?').toUpperCase()}</span>
          <Link to={`/companies/${params.row.businessId}`} className="nm">{params.value}</Link>
        </span>
      ),
    },
    {
      field: 'domain', headerName: t('business.columns.domain'), width: 180,
      renderCell: (params) => <span className="lc-email">{params.value || '—'}</span>,
    },
    { field: 'sector', headerName: t('business.columns.sector'), width: 150, valueFormatter: (value: string | null) => value ?? '—' },
    { field: 'owner', headerName: t('business.columns.owner'), width: 150, valueFormatter: (value: string | null) => value ?? '—' },
    {
      // Azioni a icona come nella lista contatti.
      field: 'actions', headerName: t('business.columns.actions'), sortable: false,
      width: 100, align: 'right', headerAlign: 'right',
      renderCell: (params) => (
        <span className="lc-actions">
          {import.meta.env.VITE_ENABLE_EDIT_FORMS === 'true' && onEditClick && (
            <button
              type="button"
              className="lc-act"
              aria-label={t('common.edit')}
              onClick={() => onEditClick(params.row.businessId)}
            >
              <EditGlyph />
            </button>
          )}
          <button
            type="button"
            className="lc-act del"
            aria-label={t('common.delete')}
            onClick={() => confirm.requestConfirm(
              t('business.deleteConfirm', { name: params.row.name }),
              () => handleDelete(params.row.businessId),
              { requireTypedConfirmation: true },
            )}
          >
            <TrashGlyph />
          </button>
        </span>
      ),
    },
  ];
}
